/**
 * ADR-0237 Slice 1 — persistent strip rendered when the dashboard is
 * running inside an agency acting-as session. Mirrors the
 * `SupportSessionBanner` shape (same slot in the shell; same height
 * class; same ending in a right-aligned action button).
 *
 * Copy: "Acting in {client} via {agency}" + "Return to {agency}".
 * The agency + client names are read from the SERVER-authoritative
 * `User.acting_context` (populated on `/v1/auth/me` from the resolved
 * Principal). No client-side persistence — the banner is visible
 * exactly when the server says the session is in an acting state.
 */

import { useState } from 'react';

import { useOptionalHttp } from '../api/http-context.js';
import { useAgency } from '../app/agency-context.js';
import { useAuth } from '../app/auth-context.js';
import { RELEASE_LANDING_PATH, releaseActingWithRetry } from '../api/agency.js';
import { useC } from '../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export function ActingBanner(): JSX.Element | null {
  const { acting } = useAgency();
  const auth = useAuth();
  const http = useOptionalHttp();
  const C = useC();
  const [releasing, setReleasing] = useState(false);
  const [releaseError, setReleaseError] = useState<string | null>(null);

  if (acting === null) return null;

  const client_label = acting.acting_account_name.length > 0
    ? acting.acting_account_name
    : acting.acting_account_id;
  const agency_label = acting.home_account_name.length > 0
    ? acting.home_account_name
    : acting.home_account_id;

  const onRelease = async (): Promise<void> => {
    if (http === null || releasing) return;
    setReleasing(true);
    setReleaseError(null);
    // The server is the authority (POST /act-as/release updates the
    // session row). Only a confirmed release reloads: reloading after a
    // failed one landed the founder in the Agency Portal still acting
    // (2026-10-28). Lands on the agency's All sites screen.
    const result = await releaseActingWithRetry(http);
    if (result.kind !== 'ok') {
      setReleasing(false);
      setReleaseError(`Could not return to ${agency_label}. ${result.message}`);
      return;
    }
    try {
      await auth.refresh();
    } catch {
      /* refresh may be rejected if the cookie was invalidated; the
         reload below falls through to Login in that case. */
    }
    if (typeof window !== 'undefined') {
      window.location.assign(RELEASE_LANDING_PATH);
    }
    // Keep `releasing` true so the button stays disabled until the
    // reload completes.
  };

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        background: C.sky,
        color: C.ink,
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        borderBottom: `1px solid ${C.border}`,
        fontFamily: sansFont,
        fontSize: 13,
        flexWrap: 'wrap',
      }}
    >
      <span
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          letterSpacing: 2,
          textTransform: 'uppercase',
          fontWeight: 600,
        }}
      >
        Agency session
      </span>
      <span style={{ opacity: 0.85 }}>
        Acting in <strong>{client_label}</strong> via <strong>{agency_label}</strong>.
      </span>
      <button
        type="button"
        onClick={() => void onRelease()}
        disabled={releasing}
        style={{
          marginLeft: 'auto',
          background: C.ink,
          color: C.sky,
          fontFamily: sansFont,
          fontSize: 12,
          fontWeight: 600,
          padding: '4px 12px',
          border: 'none',
          borderRadius: 2,
          cursor: releasing ? 'default' : 'pointer',
          opacity: releasing ? 0.7 : 1,
        }}
      >
        {releasing ? 'Returning…' : `Return to ${agency_label}`}
      </button>
      {releaseError !== null ? (
        <span data-testid="acting-banner-release-error" role="alert" style={{ flexBasis: '100%', fontSize: 12 }}>
          {releaseError}
        </span>
      ) : null}
    </div>
  );
}
